import axios from 'axios'
import {Formik} from 'formik'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Modal, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import useAppToast from 'src/hooks/useAppToast'
import {PlatformDataType} from 'src/interfaces/Platform.interface'
import {
  PLATFORM_FIELD_DATA,
  PRICE_TEMPLATE_FIELD,
  PRICE_TEMPLATE_ITEM,
  PRICE_TEMPLATE_RULE,
  OPTION_TYPE,
  PRICE_ADJUSTMENT_TYPE,
} from 'src/interfaces/Template.interface'
import {getPlatformFieldsAsync} from 'src/redux/actions/productActions'
import {
  savePlatformPriceAsync,
  updatePlatformPriceAsync,
} from 'src/redux/actions/productPricingAction'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {accessSessionTokenKeyName, loggedInAdminAccessToken} from 'src/utils/constants'
import {useUserHavingWriteAccess} from 'src/utils/useUserHavingWriteAccess'
import {useCustomerAccess} from 'src/hooks/useCustomerAccess'
import {
  shouldShowAction,
  getActionButtonProps,
  getFormFieldDisabled,
} from 'src/utils/customerAccessUtils'
import { usePermission } from 'src/hooks/usePermission'
import { usePriceModifierFields, PriceRuleFormValue, getPriceFieldDescription } from './hooks/usePriceModifierFields'
import { getPriceRuleValidationError, blockNegativeNumberInput, sanitizeNonNegativeNumericInput } from '../utils/priceModifierValidation'
import { matchPriceRuleToField } from '../utils/priceTemplateFieldMatch'
import { getRuleDateValue } from '../utils/priceRuleDateUtils'
import { normalizePlatformFieldList } from './utils/listingProfileFormPlatformUtils'
import { DynamicPriceModifierRule } from './DynamicPriceModifierRule'
type AddNewPriceTemplateModalPropsType = {
  isVisible: boolean
  closeModal: () => void
  selectedSecondaryPlatform: PlatformDataType | null
  updateItem: PRICE_TEMPLATE_ITEM | null
  getLatestPriceTemplateList: () => void
  isTemplateDetailLoading?: boolean
  initialPriceTypes?: PRICE_TEMPLATE_FIELD[] // Price types passed from button click
  hasTemplateRecords?: boolean // true when /platform-price-template returns data
}



type INITIAL_VALUES = {
  name: string
  platform_id: number
  repricing: {
    type: 'PERCENTAGE' | 'FLAT'
    value: number
  }
  isDefault: boolean
  shipping_price: {
    mapped_field: number | null
    value: number | null
  }
  priceRules: PriceRuleFormValue[]
}

const renderTooltip = (props) => (
  <Tooltip id='button-tooltip' {...props}>
    Use this Switch when you want all Products to use this Mapping/Rule.
  </Tooltip>
)

const getAdjustmentTypeFromValue = (value?: number | null): PRICE_ADJUSTMENT_TYPE => {
  if (value === undefined || value === null || value === 0) {
    return 'NO_CHANGE'
  }
  return value > 0 ? 'INCREASE' : 'DECREASE'
}

const getBackendErrorMessage = (response: any) => {
  const payload = response?.payload
  const data = payload?.data ?? payload
  const error = data?.error ?? data?.message ?? payload?.error ?? payload?.message

  if (Array.isArray(error)) {
    return error.join(', ')
  }

  if (typeof error === 'object' && error !== null) {
    return Object.values(error).flat().join(', ')
  }

  return typeof error === 'string' ? error : ''
}

const AddNewPriceTemplateModal: FC<AddNewPriceTemplateModalPropsType> = ({
  isVisible,
  closeModal,
  selectedSecondaryPlatform,
  updateItem,
  getLatestPriceTemplateList,
  isTemplateDetailLoading = false,
  initialPriceTypes = [],
  hasTemplateRecords = true,
}) => {
    const {isRead, isWrite, isDelete} = usePermission();
  const {successToast, errorToast} = useAppToast()
  const {mainStoreData, erpStoreData, omsStoreData, accountingStoreData, shippingStoreData, fetchedSourcePlatformData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const dispatch = useAppDispatch()
  const {isUserHavingWriteAccess} = useUserHavingWriteAccess()
  const customerAccess = useCustomerAccess()
  // Can store either price types (PRICE_TEMPLATE_FIELD) or price templates (from GET response)
  const [priceTemplateFields, setPriceTemplateFields] = useState<(PRICE_TEMPLATE_FIELD | any)[]>([])

  const [priceFieldsLoading, setPriceFieldsLoading] = useState(false)
  const [fetchedTemplateRules, setFetchedTemplateRules] = useState<any[] | null>(null)
  const [mappingFieldsLoading, setMappingFieldsLoading] = useState(false)
  const [nativeFields, setNativeFields] = useState<PLATFORM_FIELD_DATA[]>([])
  const [customFields, setCustomFields] = useState<PLATFORM_FIELD_DATA[]>([])
  const [loadedFieldTypes, setLoadedFieldTypes] = useState({NATIVE: false, CUSTOM: false})
  const loadingFieldTypesRef = useRef({NATIVE: false, CUSTOM: false})
  const [priceAdjustmentType, setPriceAdjustmentType] = useState<PRICE_ADJUSTMENT_TYPE>(() => {
    const type = getAdjustmentTypeFromValue(updateItem?.repricing?.value ?? 0)
    return type === 'NO_CHANGE' ? 'INCREASE' : type
  })
  const apiSource = fetchedSourcePlatformData?.source_platform
  const sourceStore = apiSource?.id
    ? apiSource
    : mainStoreData ?? omsStoreData ?? erpStoreData ?? accountingStoreData ?? shippingStoreData ?? null
  const platformIdForFields =
    selectedSecondaryPlatform?.id ?? updateItem?.platform ?? sourceStore?.id ?? null
  const mappingPlatformId = sourceStore?.id ?? null

  const { priceTemplateFields: hookFields, isLoading: hookIsLoading } = usePriceModifierFields({
    platformId: platformIdForFields,
    enabled: isVisible && (!initialPriceTypes || initialPriceTypes.length === 0)
  });
  useEffect(() => {
    const type = getAdjustmentTypeFromValue(updateItem?.repricing?.value ?? 0)
    setPriceAdjustmentType(type === 'NO_CHANGE' ? 'INCREASE' : type)
  }, [updateItem])

  const existingRules = useMemo<PRICE_TEMPLATE_RULE[]>(() => {
    if (!updateItem && !fetchedTemplateRules) {
      return []
    }
    const possibleCandidates = [
      fetchedTemplateRules, // Use fetched rules from state (priority)
      updateItem?.price_rules,
      updateItem?.template_fields,
      (updateItem as any)?.price_template_data,
      (updateItem as any)?.priceTemplateData,
    ]
    const available = possibleCandidates.find((candidate) => Array.isArray(candidate))
    if (!available) {
      return []
    }
    
    // Transform API response format to PRICE_TEMPLATE_RULE format if needed
    // API returns: {source_platform_field_id, destination_platform_field_id, destination_platform_field_data_id, ...}
    // We need: {platform_price_template_field, mapping_option, mapped_field, value, repricing, ...}
    const matchFieldFromRule = (rule: any) =>
      matchPriceRuleToField(rule, priceTemplateFields)

    return (available as any[]).map((rule: any) => {
      // If already in PRICE_TEMPLATE_RULE format, return as is
      if (rule.platform_price_template_field !== undefined) {
        return rule as PRICE_TEMPLATE_RULE
      }
      
      // Transform from API format
      // Find the price template field that matches destination_platform_field_id
      // API returns destination_platform_field_id as a number
      const destinationFieldId = rule.destination_platform_field_id || 
                                 rule.destination_platform_field?.id || 
                                 rule.destination_platform_field
      
      const matchingField = matchFieldFromRule(rule)

      // Determine mapping option based on source_platform_field_id
      const sourceFieldId = rule.source_platform_field_id || 
                           rule.source_platform_field?.id || 
                           rule.source_platform_field
      const sourceFieldType =
        rule.source_platform_field?.field_type || rule.source_platform_field?.fieldType
      const inNativeList = nativeFields.some((f) => f.id === Number(sourceFieldId))
      const inCustomList = customFields.some((f) => f.id === Number(sourceFieldId))
      const serverMappingOption = (rule.mapping_option as OPTION_TYPE) ?? undefined

      let mappingOption: 'DEFAULT' | 'NATIVE' | 'CUSTOM' = serverMappingOption ?? 'DEFAULT'
      if (mappingOption === 'DEFAULT' && sourceFieldType) {
        mappingOption = sourceFieldType === 'NATIVE' ? 'NATIVE' : 'CUSTOM'
      }
      if (mappingOption === 'DEFAULT' && inNativeList) {
        mappingOption = 'NATIVE'
      }
      if (mappingOption === 'DEFAULT' && inCustomList) {
        mappingOption = 'CUSTOM'
      }
      if (mappingOption === 'DEFAULT' && sourceFieldId) {
        // If has destination_platform_field_data_id, it's NATIVE (specific option)
        // Otherwise, fall back to CUSTOM when server did not share mapping_option
        mappingOption = rule.destination_platform_field_data_id ? 'NATIVE' : 'CUSTOM'
      }

      return {
        id: rule.id,
        platform_price_template_field: matchingField?.id,
        destination_platform_field_id: destinationFieldId,
        destination_platform_field: rule.destination_platform_field,
        destination_platform_field_api_id:
          rule?.destination_platform_field?.api_id ||
          rule?.destination_platform_field_api_id ||
          rule?.api_id ||
          matchingField?.api_id ||
          null,
        destination_platform_field_api_code:
          rule?.destination_platform_field?.api_code ||
          rule?.destination_platform_field_api_code ||
          rule?.api_code ||
          matchingField?.api_code ||
          null,
        destination_platform_field_api_name:
          rule?.destination_platform_field?.api_name ||
          rule?.destination_platform_field_api_name ||
          rule?.api_name ||
          matchingField?.api_name ||
          null,
        mapping_option: mappingOption,
        value: rule.value_str ? Number(rule.value_str) : rule.value,
        default_value: rule.value_str ? Number(rule.value_str) : rule.value,
        mapped_field: sourceFieldId || null,
        repricing: rule.repricing || {type: 'FLAT', value: 0},
        start_date: getRuleDateValue(rule, matchingField, 'start_date'),
        end_date: getRuleDateValue(rule, matchingField, 'end_date'),
      } as PRICE_TEMPLATE_RULE
    })
  }, [updateItem, priceTemplateFields, fetchedTemplateRules, nativeFields, customFields])

  const priceRulesInitialValues = useMemo<PriceRuleFormValue[]>(() => {
    const usedFieldIds = new Set<number>()
    const matchFieldForRule = (ruleAny: any, rule: PRICE_TEMPLATE_RULE, ruleIndex: number) => {
      if (!priceTemplateFields.length) {
        return undefined
      }
      const ruleId = ruleAny?.id
      const byRuleId =
        ruleId &&
        priceTemplateFields.find((field) => field.id === ruleId && !usedFieldIds.has(field.id))
      if (byRuleId) {
        usedFieldIds.add(byRuleId.id)
        return byRuleId
      }
      const apiId =
        ruleAny?.destination_platform_field_api_id ||
        ruleAny?.destination_platform_field?.api_id ||
        ruleAny?.api_id
      const apiCode =
        ruleAny?.destination_platform_field_api_code ||
        ruleAny?.destination_platform_field?.api_code ||
        ruleAny?.api_code
      const apiName =
        ruleAny?.destination_platform_field_api_name ||
        ruleAny?.destination_platform_field?.api_name ||
        ruleAny?.api_name

      const byApi = priceTemplateFields.find(
        (field) =>
          !usedFieldIds.has(field.id) &&
          ((apiId && field.api_id === apiId) ||
            (apiCode && field.api_code === apiCode) ||
            (apiName && field.api_name === apiName))
      )
      if (byApi) {
        usedFieldIds.add(byApi.id)
        return byApi
      }

      const destinationFieldId =
        ruleAny?.destination_platform_field_id ||
        ruleAny?.destination_platform_field?.id ||
        ruleAny?.destination_platform_field
      if (destinationFieldId) {
        const candidates = priceTemplateFields.filter(
          (field) =>
            field.id === destinationFieldId || field.platform_field === destinationFieldId
        )
        if (candidates.length) {
          const unused = candidates.find((field) => !usedFieldIds.has(field.id))
          const picked = unused || candidates[0]
          usedFieldIds.add(picked.id)
          return picked
        }
      }

      const byTemplateId = priceTemplateFields.find(
        (field) =>
          field.id === rule?.platform_price_template_field &&
          !usedFieldIds.has(field.id)
      )
      if (byTemplateId) {
        usedFieldIds.add(byTemplateId.id)
      }
      if (byTemplateId) {
        return byTemplateId
      }

      const indexed = priceTemplateFields[ruleIndex]
      if (indexed && !usedFieldIds.has(indexed.id)) {
        usedFieldIds.add(indexed.id)
        return indexed
      }

      const nextUnused = priceTemplateFields.find((field) => !usedFieldIds.has(field.id))
      if (nextUnused) {
        usedFieldIds.add(nextUnused.id)
      }
      return nextUnused
    }

    // When editing, create rows from existing rules (one row per rule)
    // When creating new, create rows from price fields (one row per field)
    if (updateItem?.id && existingRules.length > 0) {
      // Editing mode: create a row for each existing rule
      return existingRules.map((rule, ruleIndex) => {
        const ruleAny = rule as any
        const matchedField = matchFieldForRule(ruleAny, rule, ruleIndex)
        
        const fieldAny = matchedField as any
        const priceTypeName =
          fieldAny?.api_name ||
          fieldAny?.name ||
          ruleAny?.destination_platform_field_api_name ||
          ruleAny?.destination_platform_field?.api_name ||
          ruleAny?.api_name ||
          'Unknown'
        
        // Get source_platform_field_id from rule
        const sourceFieldId = ruleAny?.source_platform_field_id || 
                             ruleAny?.source_platform_field?.id ||
                             ruleAny?.source_platform_field ||
                             (typeof rule?.mapped_field === 'object' ? rule.mapped_field?.id : rule?.mapped_field) ||
                             null
        
        const repricingValue = rule?.repricing?.value ?? null
        const value = ruleAny?.value ?? rule?.value ?? rule?.default_value ?? null
        const mappedFieldId =
          typeof rule?.mapped_field === 'object' ? rule?.mapped_field?.id : rule?.mapped_field
        const destinationFieldDataId =
          ruleAny?.destination_platform_field_data_id ??
          ruleAny?.destination_platform_field_data?.id ??
          (Array.isArray(ruleAny?.destination_platform_field_data)
            ? ruleAny.destination_platform_field_data?.[0]?.id
            : undefined) ??
          fieldAny?.id ??
          null

        return {
          ruleId: ruleAny?.id || rule?.id || null, // Store rule ID for updates
          templateFieldId: matchedField?.id || rule?.platform_price_template_field || '',
          priceType: priceTypeName,
          isPriceDate: Boolean(fieldAny?.isPriceDate),
          mappingOption:
            (rule?.mapping_option as OPTION_TYPE) ||
            (sourceFieldId
              ? nativeFields.some((f) => f.id === Number(sourceFieldId))
                ? 'NATIVE'
                : customFields.some((f) => f.id === Number(sourceFieldId))
                ? 'CUSTOM'
                : ruleAny?.destination_platform_field_data_id
                ? 'NATIVE'
                : 'CUSTOM'
              : 'DEFAULT'),
          defaultValue: value !== null && value !== undefined ? `${value}` : '',
          mappedField: sourceFieldId || '',
          adjustmentMode: rule?.repricing?.type || 'FLAT',
          adjustmentType: getAdjustmentTypeFromValue(repricingValue),
          adjustmentValue: repricingValue && repricingValue !== 0 ? Math.abs(repricingValue).toString() : '',
          startDate: getRuleDateValue(ruleAny, fieldAny, 'start_date'),
          endDate: getRuleDateValue(ruleAny, fieldAny, 'end_date'),
          destinationPlatformFieldDataId: destinationFieldDataId ?? null, // Preserve / fallback field data ID
          description: getPriceFieldDescription(fieldAny),
        }
      })
    }
    
    // Creating new: create rows from price fields
    if (!priceTemplateFields.length) {
      return []
    }
    return priceTemplateFields.map((field, index) => {
      // Match rule by platform_price_template_field (field.id) or by destination_platform_field_id
      const matchedRule =
        existingRules.find((rule) => {
          // Direct match by platform_price_template_field
          if (rule?.platform_price_template_field === field.id) {
            return true
          }
          // Match by destination_platform_field_id (from API) matching field's platform_field
          const ruleAny = rule as any
          if (ruleAny?.destination_platform_field_id === field.platform_field) {
            return true
          }
          // Legacy matching by mapped_field
          const mappedFieldId =
            typeof rule?.mapped_field === 'object'
              ? rule?.mapped_field?.id
              : rule?.mapped_field
          return mappedFieldId === field.id
        }) || null

      const repricingValue = matchedRule?.repricing?.value ?? null
      const mappedFieldValue = (): number | '' => {
        const mapped = matchedRule?.mapped_field
        if (!mapped) {
          return ''
        }
        if (typeof mapped === 'object') {
          return mapped?.id ?? ''
        }
        return mapped
      }

      const fieldAny = field as any
      const priceTypeName = fieldAny.api_name || fieldAny.name || 'Unknown'
      
      return {
        ruleId: null, // No rule ID for new rules
        templateFieldId: field.id,
        priceType: priceTypeName,
        isPriceDate: Boolean(fieldAny?.isPriceDate),
        mappingOption: (matchedRule?.mapping_option as OPTION_TYPE) ?? 'DEFAULT',
        defaultValue:
          matchedRule?.value !== undefined && matchedRule?.value !== null
            ? `${matchedRule.value}`
            : matchedRule?.default_value !== undefined && matchedRule?.default_value !== null
            ? `${matchedRule.default_value}`
            : '',
        mappedField: mappedFieldValue(),
        adjustmentMode: matchedRule?.repricing?.type ?? fieldAny.repricing?.type ?? 'FLAT',
        adjustmentType: getAdjustmentTypeFromValue(repricingValue ?? fieldAny.repricing?.value),
        adjustmentValue:
          repricingValue && repricingValue !== 0 
            ? Math.abs(repricingValue).toString() 
            : fieldAny.repricing?.value && fieldAny.repricing.value !== 0
            ? Math.abs(fieldAny.repricing.value).toString()
            : '',
        startDate: getRuleDateValue(matchedRule, fieldAny, 'start_date'),
        endDate: getRuleDateValue(matchedRule, fieldAny, 'end_date'),
        destinationPlatformFieldDataId: null, // New rules don't have field data ID
        description: getPriceFieldDescription(fieldAny),
      }
    })
  }, [priceTemplateFields, existingRules, updateItem, nativeFields, customFields])

  const initialValues = useMemo<INITIAL_VALUES>(() => {
    const fetchedTemplateIsDefault =
      typeof (fetchedTemplateRules?.[0] as any)?.price_template?.isDefault === 'boolean'
        ? (fetchedTemplateRules?.[0] as any)?.price_template?.isDefault
        : undefined

    return {
      name: updateItem?.name ?? '', // Rule name only for editing
      platform_id: selectedSecondaryPlatform?.id ?? updateItem?.platform ?? platformIdForFields ?? 0,
      repricing: {
        type: updateItem?.repricing?.type ?? 'FLAT',
        value: updateItem?.repricing?.value ?? 0,
      },
      isDefault:
        typeof fetchedTemplateIsDefault === 'boolean'
          ? fetchedTemplateIsDefault
          : updateItem?.isDefault ?? (updateItem as any)?.price_template?.isDefault ?? false,
      shipping_price: {
        mapped_field: updateItem?.shipping_price?.mapped_field ?? null,
        value: updateItem?.shipping_price?.value ?? null,
      },
      priceRules: priceRulesInitialValues,
    }
  }, [
    platformIdForFields,
    priceRulesInitialValues,
    selectedSecondaryPlatform?.id,
    updateItem,
  ])

  // Template form toggle:
  // - priceTemplateFields is populated only when the platform exposes template price types.
  // - hasTemplateRecords is passed from caller based on list API result count.
  // We render the template-wise UI when the platform exposes price fields; otherwise fall back to legacy form.
  const hasTemplateFields = priceTemplateFields.length > 0
  const shouldUseTemplateUI = hasTemplateFields

  useEffect(() => {
    if (!isVisible) {
      setPriceTemplateFields([])
      setFetchedTemplateRules(null) // Reset fetched rules when modal closes
      setLoadedFieldTypes({NATIVE: false, CUSTOM: false})
      loadingFieldTypesRef.current = {NATIVE: false, CUSTOM: false}
      return
    }
    
    if (initialPriceTypes && initialPriceTypes.length > 0) {
      setPriceTemplateFields(initialPriceTypes)
      setPriceFieldsLoading(false)
    } else if (hookFields && hookFields.length > 0) {
      setPriceTemplateFields(hookFields)
      setPriceFieldsLoading(hookIsLoading)
    } else {
      setPriceFieldsLoading(hookIsLoading)
    }
  }, [isVisible, initialPriceTypes, hookFields, hookIsLoading])

  useEffect(() => {
    // When editing, fetch template detail
    if (isVisible && updateItem?.id && !updateItem.price_rules && !(updateItem as any).price_template_data && !fetchedTemplateRules) {
      const controller = new AbortController()
      const fetchTemplateDetails = async () => {
        try {
          const headers: Record<string, string> = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
          const token = localStorage.getItem(accessSessionTokenKeyName)
          const adminToken = localStorage.getItem(loggedInAdminAccessToken)
          if (token) headers.Authorization = `Bearer ${token}`
          if (adminToken) headers['xadmin'] = adminToken

          const detailResponse = await axios.get(
            `${process.env.REACT_APP_API_URL || ''}/platform-price-template/${updateItem.id}`,
            {
              params: {platform_id: platformIdForFields, include_price_template: true},
              headers,
              signal: controller.signal,
            }
          )
          
          const responseData = detailResponse?.data
          if (Array.isArray(responseData)) {
            setFetchedTemplateRules(responseData)
          } else if (responseData?.price_rules && Array.isArray(responseData.price_rules)) {
            setFetchedTemplateRules(responseData.price_rules)
          }
        } catch (detailError: any) {
          if (!axios.isCancel(detailError)) {
            console.error('Error loading template detail:', detailError)
          }
        }
      }
      fetchTemplateDetails()
      return () => controller.abort()
    }
  }, [isVisible, updateItem, fetchedTemplateRules, platformIdForFields])

  // Fetch master price types removed - replaced by usePriceModifierFields hook

  const loadPlatformFieldOptions = useCallback(
    async (option: OPTION_TYPE) => {
      if (option === 'DEFAULT' || !mappingPlatformId) {
        return
      }
      const fieldKey = option === 'CUSTOM' ? 'CUSTOM' : 'NATIVE'
      if (fieldKey === 'NATIVE' && (nativeFields.length || loadedFieldTypes.NATIVE)) {
        return
      }
      if (fieldKey === 'CUSTOM' && (customFields.length || loadedFieldTypes.CUSTOM)) {
        return
      }
      if (loadingFieldTypesRef.current[fieldKey]) {
        return
      }
      loadingFieldTypesRef.current = {...loadingFieldTypesRef.current, [fieldKey]: true}
      setMappingFieldsLoading(true)
      const fieldType = fieldKey
      try {
        const response = await dispatch(
          getPlatformFieldsAsync({
            platformId: mappingPlatformId,
            otherParams: {
              field_type: fieldType,
              includeLocalFields: fieldType === 'NATIVE',
              owner: 'PRODUCT',
            },
          })
        )
        const fields = normalizePlatformFieldList<PLATFORM_FIELD_DATA>(response?.payload)
        if (fieldType === 'NATIVE') {
          setNativeFields(fields)
        } else {
          setCustomFields(fields)
        }
      } catch (err) {
        console.error('Error loading mapping options', err)
      } finally {
        setMappingFieldsLoading(false)
        loadingFieldTypesRef.current = {...loadingFieldTypesRef.current, [fieldKey]: false}
        setLoadedFieldTypes((prev) => ({...prev, [fieldKey]: true}))
      }
    },
    [mappingPlatformId, nativeFields.length, customFields.length, loadedFieldTypes, dispatch]
  )

  // Always hydrate mapping dropdown options when editing so we can detect native/custom correctly
  useEffect(() => {
    if (!isVisible || !updateItem?.id || !shouldUseTemplateUI || !mappingPlatformId) {
      return
    }
    if (!nativeFields.length) {
      loadPlatformFieldOptions('NATIVE')
    }
    if (!customFields.length) {
      loadPlatformFieldOptions('CUSTOM')
    }
  }, [
    isVisible,
    updateItem?.id,
    shouldUseTemplateUI,
    mappingPlatformId,
    nativeFields.length,
    customFields.length,
    loadPlatformFieldOptions,
  ])

  useEffect(() => {
    if (!isVisible || !updateItem?.id || !shouldUseTemplateUI) {
      return
    }
    if (!priceRulesInitialValues.length) {
      return
    }

    const needsNative = priceRulesInitialValues.some((rule) => rule.mappingOption === 'NATIVE')
    const needsCustom = priceRulesInitialValues.some((rule) => rule.mappingOption === 'CUSTOM')

    if (needsNative && !nativeFields.length) {
      loadPlatformFieldOptions('NATIVE')
    }
    if (needsCustom && !customFields.length) {
      loadPlatformFieldOptions('CUSTOM')
    }
  }, [
    isVisible,
    updateItem?.id,
    shouldUseTemplateUI,
    priceRulesInitialValues,
    nativeFields.length,
    customFields.length,
    loadPlatformFieldOptions,
  ])

  const handleMappingOptionChange = async (
    rowIndex: number,
    option: OPTION_TYPE,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue(`priceRules[${rowIndex}].mappingOption`, option)
    setFieldValue(`priceRules[${rowIndex}].defaultValue`, '')
    setFieldValue(`priceRules[${rowIndex}].mappedField`, '')
    await loadPlatformFieldOptions(option)
  }

  const addProduct = async (payload, helpers) => {
    try {
      const response = await dispatch(savePlatformPriceAsync(payload))
      const backendError = getBackendErrorMessage(response)
      if (savePlatformPriceAsync.rejected.match(response) || backendError) {
        errorToast(backendError || 'Failed to add price modifier rule')
        helpers.setSubmitting(false)
        return
      }
      successToast('Added Successfully')
      getLatestPriceTemplateList()
      helpers.setSubmitting(false)
      closeModal()
    } catch (error) {
      console.error('error', error)
      helpers.setSubmitting(false)
    }
  }

  const updateProduct = async (params: {template_id: number; payload: any}, helpers) => {
    try {
      const response = await dispatch(updatePlatformPriceAsync(params))
      const backendError = getBackendErrorMessage(response)
      if (updatePlatformPriceAsync.rejected.match(response) || backendError) {
        errorToast(backendError || 'Failed to update price modifier rule')
        helpers.setSubmitting(false)
        return
      }
      successToast('Updated Successfully')
      getLatestPriceTemplateList()
      helpers.setSubmitting(false)
      closeModal()
    } catch (error) {
      console.error('error', error)
      helpers.setSubmitting(false)
    }
  }

  const buildPriceRulesPayload = (
    rules: PriceRuleFormValue[],
    templateName: string,
    platformId: number,
    repricing: {type: 'PERCENTAGE' | 'FLAT'; value: number},
    isDefault: boolean,
    shippingPrice: {mapped_field: number | null; value: number | null}
  ) => {
    // According to API docs, each price rule should be a separate object in an array
    // Each object should have: name, platform_id, repricing, isDefault, shipping_price,
    // source_platform_field_id, destination_platform_field_id, destination_platform_field_data_id, value
    return rules.map((rule) => {
      const adjustmentBase =
        rule.adjustmentType === 'NO_CHANGE'
          ? 0
          : Number(rule.adjustmentValue) || 0
      const adjustmentValue =
        rule.adjustmentType === 'DECREASE'
          ? -Math.abs(adjustmentBase)
          : Math.abs(adjustmentBase)
      
      // Find the price template field to get destination_platform_field_id and price type name
      const priceField = priceTemplateFields.find((f) => f.id === rule.templateFieldId)
      const fieldAny = priceField as any
      
      // Generate name for each rule:
      // - If templateName is provided (editing), use it
      // - Otherwise, use the price type's api_name or a default
      const ruleName = templateName.trim() || fieldAny?.api_name || 'Price Rule'
      
      // According to API docs:
      // - source_platform_field_id: Field ID from main/source platform (for NATIVE/CUSTOM mapping)
      // - destination_platform_field_id: Field ID from destination platform (price template field's platform_field)
      // - destination_platform_field_data_id: Field data ID for specific option/value mapping
      // - value: Used for DEFAULT option
      
      // When user selects NATIVE/CUSTOM, mappedField is a platform field ID from main store
      // This becomes the source_platform_field_id
      const sourcePlatformFieldId = 
        rule.mappingOption !== 'DEFAULT' && rule.mappedField !== ''
          ? Number(rule.mappedField)
          : null
      
      // destination_platform_field_id is always the platform_field from the price template field
      const destinationPlatformFieldId = priceField?.platform_field || null
      
      // destination_platform_field_data_id: Use from rule, or fallback to the first available field data id
      const destinationPlatformFieldDataId =
        rule.destinationPlatformFieldDataId ||
        fieldAny?.id ||
        null
      
      // value is used for DEFAULT option
      const value = 
        rule.mappingOption === 'DEFAULT' && rule.defaultValue !== ''
          ? Number(rule.defaultValue)
          : null
      
      const payload: any = {
        name: ruleName,
        platform_id: platformId,
        repricing: {
          type: rule.adjustmentMode,
          value: rule.adjustmentType === 'NO_CHANGE' ? 0 : adjustmentValue,
        },
        isDefault: isDefault,
        shipping_price: shippingPrice,
        source_platform_field_id: sourcePlatformFieldId,
        destination_platform_field_id: destinationPlatformFieldId,
        destination_platform_field_data_id: destinationPlatformFieldDataId,
        value: value,
      }

      if (rule.isPriceDate) {
        payload.start_date = rule.startDate || null
        payload.end_date = rule.endDate || null
      }
      
      // Include rule ID when updating (required for update API)
      if (rule.ruleId) {
        payload.id = rule.ruleId
      }
      
      return payload
    })
  }

  return (
    <Modal size='xl' backdrop='static' centered show={isVisible} onHide={closeModal}>
        <Formik
        enableReinitialize
          initialValues={initialValues}
          onSubmit={(values, helpers) => {
            const platformId = values.platform_id || platformIdForFields || 0
            if (updateItem?.id && !values.name.trim()) {
              errorToast('Rule name is required.')
              helpers.setSubmitting(false)
              return
            }

            // Fallback to legacy form when no template fields are available
            if (!shouldUseTemplateUI) {
              if (!platformId) {
                errorToast('Platform ID is required.')
                helpers.setSubmitting(false)
                return
              }
              const baseValue = Number(values.repricing.value) || 0
              const adjustedValue =
                priceAdjustmentType === 'DECREASE'
                  ? -Math.abs(baseValue)
                  : Math.abs(baseValue)

              const legacyPayload = {
                name: values.name,
                platform_id: platformId,
                repricing: {
                  type: values.repricing.type,
                  value: adjustedValue,
                },
                isDefault: values.isDefault,
                shipping_price: values.shipping_price,
              }

              if (updateItem?.id) {
                updateProduct({template_id: updateItem.id, payload: legacyPayload}, helpers)
              } else {
                addProduct(legacyPayload, helpers)
              }
              return
            }

            if (!values.priceRules.length) {
              errorToast('No platform price types found to configure.')
              helpers.setSubmitting(false)
              return
            }
            const priceRuleValidationError = getPriceRuleValidationError(values.priceRules)
            if (priceRuleValidationError) {
              errorToast(priceRuleValidationError)
              helpers.setSubmitting(false)
              return
            }

            if (!platformId) {
              errorToast('Platform ID is required.')
              helpers.setSubmitting(false)
              return
            }

            const payload = buildPriceRulesPayload(
              values.priceRules,
              values.name,
              platformId,
              values.repricing,
              values.isDefault,
              values.shipping_price
            )

            // Ensure payload is an array (not an object)
            const payloadArray = Array.isArray(payload) ? payload : Object.values(payload)

            // Validate platform_id is present in all items
            const hasValidPlatformId = payloadArray.every(
              (item: any) => item.platform_id && item.platform_id > 0
            )
            if (!hasValidPlatformId) {
              errorToast('Platform ID is required for all rules.')
              helpers.setSubmitting(false)
              return
            }

            if (updateItem?.id) {
              updateProduct({template_id: updateItem.id, payload: payloadArray}, helpers)
            } else {
              addProduct(payloadArray, helpers)
            }
          }}
      >
        {({values, handleChange, handleSubmit, setFieldValue, isSubmitting}) => {
          const hasPriceDateRows = values.priceRules.some((rule) => rule.isPriceDate)
          const priceRuleGridStyle: React.CSSProperties = {
            display: 'grid',
            gridTemplateColumns: hasPriceDateRows
              ? '130px 145px 145px minmax(300px, 1fr) 130px 130px'
              : '160px 190px 190px minmax(360px, 1fr)',
            columnGap: hasPriceDateRows ? '12px' : '20px',
            width: '100%',
            minWidth: hasPriceDateRows ? '0' : '820px',
          }
          const priceRuleCellStyle: React.CSSProperties = {minWidth: 0}
          const disableSaveButton =
            isSubmitting ||
            priceFieldsLoading ||
            isTemplateDetailLoading ||
            !isUserHavingWriteAccess() ||
            getFormFieldDisabled(customerAccess) ||
            (shouldUseTemplateUI && !values.priceRules.length)

            return (
                <form onSubmit={handleSubmit}>
                  <Modal.Header>
                    {updateItem?.id ? (
                  <Modal.Title>Update Price Modifier Rule</Modal.Title>
                    ) : (
                  <Modal.Title>Add New Price Modifier Rule</Modal.Title>
                    )}

                    <div className='d-inline-flex'>
                      <Button
                        size='sm'
                        className='mx-2'
                        type='button'
                        onClick={() => {
                          closeModal()
                        }}
                      >
                      Close
                    </Button>
                    {isWrite && shouldShowAction(customerAccess, 'edit') && (
                      <Button
                        size='sm'
                        variant='success'
                        {...getActionButtonProps(customerAccess, 'edit')}
                        disabled={disableSaveButton}
                        type='submit'
                      >
                        {isSubmitting ? 'Saving' : 'Save Changes'}
                      </Button>
                    )}
                  </div>
                </Modal.Header>
                <Modal.Body>
                {isTemplateDetailLoading ? (
                  <div className='py-10 text-center'>
                    <Spinner animation='border' variant='primary' role='status' />
                    <p className='mt-3 mb-0'>Loading price modifier rule...</p>
                  </div>
                ) : (
                    <>
                    <div className='d-flex justify-content-end form-check form-switch mt-2'>
                      <div>
                        <label className='form-label mt-1'>
                          Default&nbsp;
                          <OverlayTrigger placement='left' overlay={renderTooltip}>
                            <i className='bi bi-info-circle'></i>
                          </OverlayTrigger>
                        </label>
                      </div>
                      <input
                        className='ms-4 mb-4 form-check-input h-20px w-40px'
                        type='checkbox'
                        checked={values.isDefault}
                        name='isDefault'
                        onChange={handleChange}
                      />
                    </div>

                    {/* Rule name shown for both legacy and template flows */}
                    <div className='mb-8'>
                      <label className='form-label'>Rule name</label>
                      <input
                        type='text'
                        autoComplete='off'
                        className='form-control'
                        placeholder='Enter name'
                        name='name'
                        value={values.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div className='border rounded p-4'>
                      {priceFieldsLoading ? (
                        <div className='py-10 text-center'>
                          <Spinner animation='border' variant='primary' role='status' />
                          <p className='mt-3 mb-0'>Loading price fields...</p>
                        </div>
                      ) : shouldUseTemplateUI ? (
                        <>
                          <DynamicPriceModifierRule
                            priceRules={values.priceRules}
                            isLoading={false}
                            onChange={(index, field, value) => setFieldValue(`priceRules[${index}].${field as string}`, value)}
                            advancedMode={true}
                            hasPriceDateRows={hasPriceDateRows}
                            nativeFields={nativeFields}
                            customFields={customFields}
                            mappingFieldsLoading={mappingFieldsLoading}
                            onMappingOptionChange={(index, option) =>
                              handleMappingOptionChange(index, option, setFieldValue)
                            }
                            fallbackUI={
                              <div className='py-5 text-center text-muted'>
                                No platform price fields available for this marketplace yet.
                              </div>
                            }
                          />
                        </>
                      ) : (
                        <div className='row'>
                          <div className='col-sm-3 col-12 align-items-center d-flex'>
                            <p className='form-label text-center mb-0'>Price Adjustment</p>
                          </div>
                          <div className='col-sm-3 col-12 mt-2 mt-sm-0'>
                            <select
                              className='form-select'
                              value={priceAdjustmentType}
                              onChange={(ev) =>
                                setPriceAdjustmentType(ev.target.value as PRICE_ADJUSTMENT_TYPE)
                              }
                            >
                              <option value={'INCREASE'}>Increase</option>
                              <option value={'DECREASE'}>Decrease</option>
                            </select>
                          </div>
                          <div className='col-sm-3 col-12 mt-2 mt-sm-0'>
                            <div className='d-flex align-items-center '>
                              <label className='form-label mb-0'>by</label>
                              <input
                                type='number'
                                className='ms-2 form-control '
                                placeholder='Enter value'
                                name='repricing.value'
                                min={0}
                                step='any'
                                value={Math.abs(Number(values.repricing.value) || 0)}
                                onKeyDown={blockNegativeNumberInput}
                                onChange={(event) => {
                                  const sanitized = sanitizeNonNegativeNumericInput(event.target.value)
                                  setFieldValue('repricing.value', sanitized === '' ? 0 : Number(sanitized))
                                }}
                              />
                            </div>
                          </div>
                          <div className='col-sm-3 col-12 mt-2 mt-sm-0'>
                            <select
                              className='form-select'
                              name='repricing.type'
                              onChange={handleChange}
                              value={values.repricing.type}
                            >
                              <option value={'FLAT'}>Flat Amount</option>
                              <option value={'PERCENTAGE'}>Percent</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                  </Modal.Body>
                </form>
            )
          }}
        </Formik>
      </Modal>
  )
}

export default AddNewPriceTemplateModal

